import { useEffect, useRef } from 'react';

// Digital-connections globe cursor (futuristic pointer)
const GlobeCursor = () => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    // Skip on touch / reduced-motion
    if (window.matchMedia('(hover: none)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const el = ref.current;
    el.style.opacity = '0';

    let tx = window.innerWidth / 2;
    let ty = window.innerHeight / 2;
    let x = tx;
    let y = ty;
    let shown = false;

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
      if (!shown) {
        shown = true;
        el.style.opacity = '1';
      }
    };
    const onLeave = () => {
      el.style.opacity = '0';
      shown = false;
    };
    const onEnter = () => {
      el.style.opacity = '1';
      shown = true;
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('mouseenter', onEnter);

    let raf;
    const loop = () => {
      x += (tx - x) * 0.42;
      y += (ty - y) * 0.42;
      el.style.transform = `translate3d(${x - 20}px, ${y - 20}px, 0)`;

      // Interactive-element hover detection
      const hovered = document.elementFromPoint(tx, ty);
      if (hovered && hovered.closest('a, button, input, textarea, select, [role="button"], [data-testid]')) {
        el.classList.add('cursor-active');
      } else {
        el.classList.remove('cursor-active');
      }

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('mouseenter', onEnter);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div ref={ref} className="globe-cursor" aria-hidden="true" data-testid="globe-cursor">
      <svg viewBox="0 0 40 40" width="40" height="40" className="globe-svg">
        <defs>
          <radialGradient id="globe-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#057EF3" stopOpacity="0.55" />
            <stop offset="70%" stopColor="#057EF3" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#057EF3" stopOpacity="0" />
          </radialGradient>
          <filter id="globe-blur" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="0.35" />
          </filter>
        </defs>

        {/* halo */}
        <circle cx="20" cy="20" r="19" fill="url(#globe-glow)" />

        {/* sphere outline */}
        <circle cx="20" cy="20" r="11" fill="none" stroke="#9fd1ff" strokeWidth="0.7" opacity="0.85" />

        {/* rotating meridians group */}
        <g className="globe-spin" style={{ transformOrigin: '20px 20px' }} filter="url(#globe-blur)">
          <ellipse cx="20" cy="20" rx="3.2" ry="11" fill="none" stroke="#8ed0ff" strokeWidth="0.55" opacity="0.95" />
          <ellipse cx="20" cy="20" rx="7" ry="11" fill="none" stroke="#5fbaff" strokeWidth="0.5" opacity="0.7" />
          <ellipse cx="20" cy="20" rx="10.2" ry="11" fill="none" stroke="#3aa5ff" strokeWidth="0.45" opacity="0.5" />
        </g>

        {/* parallels (static) */}
        <ellipse cx="20" cy="20" rx="11" ry="3" fill="none" stroke="#8ed0ff" strokeWidth="0.45" opacity="0.55" />
        <ellipse cx="20" cy="20" rx="11" ry="7" fill="none" stroke="#8ed0ff" strokeWidth="0.35" opacity="0.35" />

        {/* nodes */}
        <g className="globe-nodes">
          <circle cx="20" cy="9"  r="1" fill="#ffffff" />
          <circle cx="20" cy="31" r="1" fill="#ffffff" />
          <circle cx="9"  cy="20" r="1" fill="#57b4f5" />
          <circle cx="31" cy="20" r="1" fill="#57b4f5" />
          <circle cx="13" cy="13" r="0.8" fill="#9fd1ff" />
          <circle cx="27" cy="27" r="0.8" fill="#9fd1ff" />
        </g>

        {/* center pulse */}
        <circle cx="20" cy="20" r="1.4" fill="#057EF3" className="globe-center" />
      </svg>
    </div>
  );
};

export default GlobeCursor;
